import { useTranslation } from 'react-i18next'

import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { cn } from '@/lib/utils'
import { FieldTooltip } from '../FieldLabel'

export interface NegateToggleProps {
  negated: boolean
  onChange: (negated: boolean) => void
  /** What is being included or excluded, e.g. "sites". */
  what: string
  disabled?: boolean
  /** Short "Include" / "Exclude" labels, for narrow spots such as a picker column. */
  compact?: boolean
  /** Replaces the include/exclude explanation in the tooltip. */
  help?: string
}

/** The two states, as the string values Radix tabs address them by. */
const INCLUDE = 'include'
const EXCLUDE = 'exclude'

/**
 * Include/exclude switch for a target that can be negated: targeting what is
 * selected, or everything except it.
 *
 * Built on the shadcn {@link Tabs} primitive so it looks and behaves like every
 * other tab strip in the app — there are no panels, the choice itself is what
 * the two tabs stand for.
 */
export function NegateToggle({
  negated,
  onChange,
  what,
  disabled,
  compact,
  help,
}: NegateToggleProps) {
  const { t } = useTranslation()

  return (
    <div className="flex items-center gap-2">
      <Tabs
        value={negated ? EXCLUDE : INCLUDE}
        onValueChange={(value) => onChange(value === EXCLUDE)}
      >
        <TabsList className={cn(compact && 'h-7')}>
          <TabsTrigger
            value={INCLUDE}
            disabled={disabled}
            className={cn(compact && 'px-2 text-xs')}
          >
            {t(compact ? 'targeting.includeShort' : 'targeting.include')}
          </TabsTrigger>
          {/* An active exclude takes the red the exclude chips are drawn in, so
              the mode can be read at a glance. */}
          <TabsTrigger
            value={EXCLUDE}
            disabled={disabled}
            className={cn(
              'data-[state=active]:border-(--ad-badge-red-border) data-[state=active]:bg-(--ad-badge-red-bg) data-[state=active]:text-(--ad-badge-red) dark:data-[state=active]:border-(--ad-badge-red-border) dark:data-[state=active]:bg-(--ad-badge-red-bg) dark:data-[state=active]:text-(--ad-badge-red)',
              compact && 'px-2 text-xs'
            )}
          >
            {t(compact ? 'targeting.excludeShort' : 'targeting.exclude')}
          </TabsTrigger>
        </TabsList>
      </Tabs>
      <FieldTooltip
        text={
          help ?? `${t('targeting.includeHelp', { what })}\n${t('targeting.excludeHelp', { what })}`
        }
      />
    </div>
  )
}
